import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, ArrowRight, CalendarBlank, CheckCircle, Rainbow, Car, Compass,
  MapTrifold, Binoculars, ForkKnife, SunHorizon, Clock, Ticket, House, MapPin, AirplaneTilt, Bicycle,
} from "@phosphor-icons/react";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd, faqJsonLd } from "@/lib/schema";
import { properties } from "@/data/properties";
import { eventGuides, EVENT_PLACES } from "@/data/eventGuides";
import { formatRange, INTL_LOCALE } from "@/pages/EventsPage";
import { countdownLabel, countdownTier } from "@/lib/dates";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function EventGuide({ config }) {
  const { lang, t } = useLanguage();
  const loc = INTL_LOCALE[lang] || "en-US";
  const [live, setLive] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/events`)
      .then((r) => {
        if (!alive) return;
        const ev = (r.data.events || []).find((e) => e.id === config.eventId);
        if (ev) setLive(ev);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [config.eventId]);

  // Live occurrence wins; fall back to the config's known dates (what the
  // prerendered static HTML carries, and what shows if the API is unreachable).
  const start = live?.start || config.fallback.start;
  const end = live?.end || config.fallback.end;
  const checkout = live?.checkout || config.fallback.checkout;
  const year = (live?.start || config.fallback.start).slice(0, 4);
  const datesLabel = live ? formatRange(live.start, live.end, loc) : config.fallback.label;
  const confirmed = live ? live.confirmed : true;

  // Translate the template first (dictionary keys carry the {year}/{dates}
  // tokens), then fill the tokens — so these strings localize cleanly.
  const fill = (s) =>
    t(s)
      .replaceAll("{year}", year)
      .replaceAll("{dates}", datesLabel)
      .replaceAll("{datesShort}", datesLabel.replace(/, \d{4}$/, ""));

  const homes = config.propertySlugs
    .map((slug) => properties.find((p) => p.slug === slug))
    .filter(Boolean);
  const others = eventGuides.filter((g) => g.slug !== config.slug);

  const place = EVENT_PLACES[config.eventId] || {};
  const eventLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: config.eventName,
    startDate: start,
    endDate: end,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description: fill(config.answer),
    url: config.officialUrl,
    location: {
      "@type": "Place",
      name: live?.venue || place.venue || config.eventName,
      address: { "@type": "PostalAddress", addressLocality: live?.location || place.city || "Fort Lauderdale", addressRegion: "FL", addressCountry: "US" },
    },
    organizer: { "@id": `${SITE}/#business` },
  };

  return (
    <main data-testid="event-guide" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title={fill(config.metaTitle)}
        description={fill(config.metaDescription)}
        canonical={`${SITE}/events/${config.slug}`}
        image={config.ogImage ? `${SITE}${config.ogImage}` : undefined}
        jsonLd={[
          eventLd,
          faqJsonLd(config.faqs.map((f) => ({ q: fill(f.q), a: fill(f.a) }))),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Events", path: "/events" },
            { name: config.shortTitle, path: `/events/${config.slug}` },
          ]),
        ]}
      />

      {/* Hero + direct answer */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <Link to="/events" data-testid="event-guide-back" className="inline-flex items-center gap-2 text-sm text-emerald-soft hover:text-sunset transition-colors duration-300 mb-8">
            <ArrowLeft size={15} weight="bold" /> Back to all events
          </Link>
          <p className="overline text-sunset mb-5 flex items-center gap-2">
            <Compass size={16} weight="fill" /> {config.category}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            {fill(config.h1)}
          </h1>
          <div data-testid="event-guide-answer" className="text-lg md:text-xl text-emerald-soft font-light leading-relaxed max-w-3xl mt-7">
            {fill(config.answer).split("\n\n").map((para, i) => (
              <p key={i} className="mb-5 last:mb-0">{para}</p>
            ))}
          </div>
        </motion.div>

        {/* At a glance summary card — sits immediately below the intro (optional per-guide field) */}
        {config.atGlance && (
          <motion.div {...fade(0.06)} data-testid="event-guide-at-glance" className="mt-10">
            <div className="rounded-2xl border border-emerald/12 bg-sand/60 px-6 md:px-10 py-7 md:py-9">
              <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight mb-6">{fill(config.atGlance.h)}</h2>
              <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
                {config.atGlance.items.map((item, i) => {
                  const GlanceIcon = { calendar: CalendarBlank, pin: MapPin, clock: Clock, ticket: Ticket, car: Car, house: House, airplane: AirplaneTilt, bicycle: Bicycle }[item.icon] || CheckCircle;
                  return (
                    <li key={i} className="flex items-center gap-3 text-emerald font-light">
                      <span className="w-9 h-9 rounded-full bg-sunset/10 grid place-items-center shrink-0">
                        <GlanceIcon size={18} weight="duotone" className="text-sunset" />
                      </span>
                      <span>{fill(item.text)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>
        )}

        {/* Next edition banner */}
        <motion.div
          {...fade(0.1)}
          data-testid="event-guide-dates"
          className="mt-10 rounded-3xl border border-emerald/12 bg-sand-2 p-7 md:p-9 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        >
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-full bg-emerald grid place-items-center shrink-0">
              <CalendarBlank size={22} weight="duotone" className="text-sand" />
            </span>
            <div>
              <p className="overline text-sunset mb-1">Next edition</p>
              <p className="font-display text-2xl md:text-3xl text-emerald leading-tight" data-testid="event-guide-date-label">
                {datesLabel}
              </p>
              <span
                data-testid="event-guide-countdown"
                className={`mt-2 inline-flex w-fit items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                  countdownTier(start) === "near"
                    ? "bg-sunset text-sand"
                    : countdownTier(start) === "mid"
                    ? "bg-gold/25 text-emerald"
                    : "bg-emerald/8 text-emerald-soft"
                }`}
              >
                {countdownLabel(start)}
              </span>
              {!confirmed && (
                <p className="text-xs text-emerald-soft/80 mt-1">Dates approximate — confirm on the official page</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:ml-auto">
            <Link
              to={`/properties?checkin=${start}&checkout=${checkout}#book`}
              data-testid="event-guide-book"
              className="group inline-flex items-center gap-2 rounded-full bg-sunset px-6 py-3 text-sm font-medium text-sand transition-colors duration-300 hover:bg-emerald"
            >
              Book your stay for these dates
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={config.officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="event-guide-official"
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald/25 px-6 py-3 text-sm font-medium text-emerald transition-colors duration-300 hover:bg-emerald/5"
            >
              Official event page <ArrowUpRight size={15} weight="bold" />
            </a>
          </div>
        </motion.div>
      </div>

      {/* Photo strip (optional per-guide field) */}
      {config.photos && (
        <motion.div {...fade(0.05)} data-testid="event-guide-photos" className="max-w-[1100px] mx-auto px-6 md:px-10 mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {config.photos.map((p, i) => (
            <figure key={i} className="group">
              <div className="rounded-2xl overflow-hidden border border-emerald/10">
                <img
                  src={`https://images.unsplash.com/${p.src}?crop=entropy&fit=crop&auto=format&q=80&w=900`}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <figcaption className="mt-3 text-sm text-emerald-soft/80 font-light">{p.caption}</figcaption>
            </figure>
          ))}
        </motion.div>
      )}

      {/* Route/schedule + best viewing (optional per-guide fields) */}
      {(config.route || config.viewingSpots) && (
        <div className={`max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28 grid gap-x-16 gap-y-14${config.route && config.viewingSpots ? " md:grid-cols-2" : ""}`}>
          {config.route && (
            <motion.section {...fade()} data-testid="event-guide-route">
              <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-5">
                <MapTrifold size={20} weight="duotone" className="text-sunset" />
                <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{config.route.h}</h2>
              </div>
              {config.route.paragraphs.map((p, i) => (
                <p key={i} className="text-emerald-soft font-light leading-relaxed mb-4 last:mb-0">{p}</p>
              ))}
            </motion.section>
          )}
          {config.viewingSpots && (
            <motion.section {...fade(0.06)} data-testid="event-guide-viewing">
              <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-5">
                <Binoculars size={20} weight="duotone" className="text-sunset" />
                <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{config.viewingSpots.h}</h2>
              </div>
              <ul className="flex flex-col gap-3.5">
                {config.viewingSpots.items.map((k, i) => (
                  <li key={i} className="flex gap-3 text-emerald-soft font-light leading-relaxed">
                    <CheckCircle size={18} weight="fill" className="text-sunset shrink-0 mt-1" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}
        </div>
      )}

      {/* Prose viewing guide (optional per-guide field; paragraphs starting "DRS tip:" render as a callout) */}
      {config.viewingGuide && (
        <motion.section {...fade()} data-testid="event-guide-viewing" className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
          <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-6">
            <Binoculars size={20} weight="duotone" className="text-sunset" />
            <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{config.viewingGuide.h}</h2>
          </div>
          <div className="max-w-3xl">
            {config.viewingGuide.paragraphs.map((p, i) =>
              p.startsWith("DRS tip:") ? (
                <div key={i} className="my-6 rounded-2xl border border-sunset/25 bg-sunset/5 px-5 py-4">
                  <p className="text-emerald font-light leading-relaxed">
                    <span className="font-semibold text-sunset">DRS tip:</span>{p.slice("DRS tip:".length)}
                  </p>
                </div>
              ) : (
                <p key={i} className="text-emerald-soft font-light leading-relaxed mb-4 last:mb-0">{p}</p>
              )
            )}
            {config.viewingGuide.sub && (
              <>
                <h3 className="font-display text-xl md:text-2xl leading-snug tracking-tight mt-10 mb-4">{config.viewingGuide.sub.h}</h3>
                {config.viewingGuide.sub.paragraphs.map((p, i) => (
                  <p key={i} className="text-emerald-soft font-light leading-relaxed mb-4 last:mb-0">{p}</p>
                ))}
              </>
            )}
          </div>
        </motion.section>
      )}

      {/* Where to stay */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <motion.h2 {...fade()} className="font-display text-3xl md:text-4xl tracking-tight mb-4">
          Where to stay
        </motion.h2>
        <motion.p {...fade()} className="text-emerald-soft font-light max-w-2xl mb-10">
          {config.stayNote}
        </motion.p>
        <div className="grid sm:grid-cols-2 gap-6">
          {homes.map((p, i) => (
            <motion.div key={p.slug} {...fade((i % 2) * 0.06)}>
              <Link
                to={`/properties/${p.slug}`}
                data-testid={`event-guide-home-${p.slug}`}
                className="group block rounded-2xl overflow-hidden border border-emerald/12 bg-sand-2 transition-colors duration-300 hover:border-sunset/40"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-xl md:text-2xl leading-snug text-emerald">{p.shortName}</p>
                    <p className="text-emerald-soft text-sm mt-1.5">
                      {p.location} · sleeps {p.guests} · {p.bedrooms} bd · {p.bathrooms} ba
                    </p>
                  </div>
                  <ArrowUpRight size={22} className="shrink-0 text-sunset transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        {config.whyStay && (
          <motion.div {...fade()} data-testid="event-guide-why-stay" className="mt-14 max-w-3xl">
            <h3 className="font-display text-2xl md:text-3xl leading-snug tracking-tight mb-4">{config.whyStay.h}</h3>
            <p className="text-emerald-soft font-light leading-relaxed">{config.whyStay.text}</p>
          </motion.div>
        )}
      </div>

      {/* Getting there + things to know */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28 grid md:grid-cols-2 gap-x-16 gap-y-14">
        <motion.section {...fade()} data-testid="event-guide-getting-there">
          <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-5">
            <Car size={20} weight="duotone" className="text-sunset" />
            <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">Getting there & parking</h2>
          </div>
          {config.gettingThere.map((p, i) => (
            <p key={i} className="text-emerald-soft font-light leading-relaxed mb-4 last:mb-0">{p}</p>
          ))}
        </motion.section>
        <motion.section {...fade(0.06)} data-testid="event-guide-know">
          <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-5">
            <CheckCircle size={20} weight="duotone" className="text-sunset" />
            <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">Good to know before you go</h2>
          </div>
          <ul className="flex flex-col gap-3.5">
            {config.knowBefore.map((k, i) => (
              <li key={i} className="flex gap-3 text-emerald-soft font-light leading-relaxed">
                <CheckCircle size={18} weight="fill" className="text-sunset shrink-0 mt-1" />
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </motion.section>
      </div>

      {/* Restaurants + weekend ideas (optional per-guide fields) */}
      {(config.restaurants || config.weekendExtras) && (
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28 grid md:grid-cols-2 gap-x-16 gap-y-14">
          {config.restaurants && (
            <motion.section {...fade()} data-testid="event-guide-restaurants">
              <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-5">
                <ForkKnife size={20} weight="duotone" className="text-sunset" />
                <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{config.restaurants.h}</h2>
              </div>
              <ul className="flex flex-col gap-3.5">
                {config.restaurants.items.map((k, i) => (
                  <li key={i} className="flex gap-3 text-emerald-soft font-light leading-relaxed">
                    <CheckCircle size={18} weight="fill" className="text-sunset shrink-0 mt-1" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}
          {config.weekendExtras && (
            <motion.section {...fade(0.06)} data-testid="event-guide-weekend">
              <div className="flex items-center gap-3 border-t border-emerald/15 pt-6 mb-5">
                <SunHorizon size={20} weight="duotone" className="text-sunset" />
                <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{config.weekendExtras.h}</h2>
              </div>
              <ul className="flex flex-col gap-3.5">
                {config.weekendExtras.items.map((k, i) => (
                  <li key={i} className="flex gap-3 text-emerald-soft font-light leading-relaxed">
                    <CheckCircle size={18} weight="fill" className="text-sunset shrink-0 mt-1" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}
        </div>
      )}

      {/* Host tip */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-16 md:mt-20">
        <motion.div
          {...fade()}
          data-testid="event-guide-host-tip"
          className="rounded-3xl bg-emerald text-sand p-8 md:p-12 flex flex-col md:flex-row gap-6 md:gap-10 md:items-center"
        >
          <span className="w-14 h-14 rounded-full bg-sunset grid place-items-center shrink-0">
            <Rainbow size={26} weight="fill" className="text-sand" />
          </span>
          <div>
            <p className="overline text-gold mb-2">Bill &amp; Jonathan's pick</p>
            <p className="font-display text-xl md:text-2xl leading-snug font-light">{config.hostTip}</p>
          </div>
        </motion.div>
      </div>

      {/* FAQ */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <motion.h2 {...fade()} className="font-display text-3xl md:text-4xl tracking-tight mb-8">
          Frequently asked
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-9">
          {config.faqs.map((f, i) => (
            <motion.div key={i} {...fade((i % 2) * 0.06)} className="border-t border-emerald/15 pt-6" data-testid={`event-guide-faq-${i}`}>
              <h3 className="font-display text-lg md:text-xl leading-snug text-emerald mb-3">{fill(f.q)}</h3>
              <p className="text-emerald-soft font-light leading-relaxed">{fill(f.a)}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* More event guides */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <motion.h2 {...fade()} className="font-display text-2xl md:text-3xl tracking-tight mb-6">
          More event guides
        </motion.h2>
        <div className="flex flex-wrap gap-3" data-testid="event-guide-related">
          {others.map((g) => (
            <Link
              key={g.slug}
              to={`/events/${g.slug}`}
              data-testid={`event-guide-related-${g.slug}`}
              className="rounded-full border border-emerald/20 px-5 py-2.5 text-sm text-emerald transition-colors duration-300 hover:border-sunset hover:text-sunset"
            >
              {g.shortTitle}
            </Link>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div {...fade()} className="rounded-3xl bg-emerald text-sand p-10 md:p-14 text-center" data-testid="event-guide-cta">
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight max-w-2xl mx-auto">
            Find your dates, then find your suite.
          </h2>
          <p className="text-sand/70 mt-4 max-w-xl mx-auto font-light">
            Book direct for the best rate, real-time availability and a host who actually replies.
          </p>
          <Link
            to={`/properties?checkin=${start}&checkout=${checkout}#book`}
            data-testid="event-guide-cta-book"
            className="group inline-flex items-center gap-2 mt-8 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-gold hover:text-emerald"
          >
            Book your stay for these dates
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
